import { createApp, h, type DefineComponent } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import PrimeVue from 'primevue/config'
import '@/assets/tailwind.css'
import { Link } from '@inertiajs/vue3'
import type { CurrentUserType } from '@/composables/useCurrentUser'
import { Settings } from 'luxon'
import { MusthadPreset } from '@/theme'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { i18n } from '@/services/i18n'
import { initSentry } from '@/services/sentry'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { setOptions as setGoogleMapsOptions } from '@googlemaps/js-api-loader'

setGoogleMapsOptions({
  key: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
  v: 'weekly',
  libraries: ['places'],
})

createInertiaApp({
  progress: {
    color: '#E39300',
  },
  resolve: (name) => {
    const pages = import.meta.glob<DefineComponent>(
      './modules/**/pages/*.page.vue',
    )
    const pageModule = name.split('/')[0]
    const pageFile = name.split('/')[1]
    const importPage =
      pages[`./modules/${pageModule}/pages/${pageFile}.page.vue`]
    return importPage().then((module) => {
      module.default.layout = module.default.layout || AppLayout
      return module.default
    })
  },
  setup({ el, App, props, plugin }) {
    const authProps = props.initialPage.props.auth as {
      user: CurrentUserType
    }
    const currentUser = authProps?.user
    const locale = currentUser?.language?.code ?? 'en'

    i18n.global.locale.value = locale
    Settings.defaultLocale = locale

    const app = createApp({ render: () => h(App, props) })
      .use(VueQueryPlugin)
      .use(i18n)
      .use(PrimeVue, {
        pt: {
          accordioncontent: {
            content: '!p-0 text-surface-950',
          },
          message: {
            root: ({ props }) => ({
              class: { 'p-message-sm': !props.size },
            }),
          },
        },
        theme: {
          preset: MusthadPreset,
          options: {
            // (!) DO NOT CHANGE THE PREFIX (!)
            // https://github.com/primefaces/tailwindcss-primeui/blob/main/src/theme.js
            // You can see that they are using 'p' as the prefix, so if you
            // change it, the integration will break.
            prefix: 'p',
            darkModeSelector: '.dark',
            cssLayer: {
              name: 'primevue',
              order: 'tailwind-base, primevue, tailwind-utilities',
            },
          },
        },
      })
      .use(ToastService)
      .use(ConfirmationService)
      .use(plugin)
      .component('InertiaLink', Link)

    if (import.meta.env.VITE_SENTRY_DNS !== 'DISABLED') {
      initSentry(app)
    }

    app.mount(el)
  },
}).then(() => {
  router.on('navigate', () => {
    if (window.location.hash) {
      requestAnimationFrame(() => {
        document.querySelector(window.location.hash)?.scrollIntoView()
      })
    }
  })

  console.log('App ready.')
})
